import type { TFunction } from 'i18next';

import type { SolutionItemProps } from './solutionItemTypes';

type ArchitectureItem = Omit<
  SolutionItemProps,
  'idPrefix' | 'index' | 'isDesktop'
>;

/**
 * ### getEdgeArchitectureItem
 *
 * Safety 페이지에 이식하는 엣지 분석서버 방식 데이터.
 * 기존 `/technology` HowItWorksSection 의 첫 번째 항목을 그대로 옮겼다.
 * 키는 `common` 네임스페이스의 기존 번역 키를 재사용하므로 로케일 추가가 없다.
 *
 * @param t `common` 네임스페이스 번역 함수
 * @returns 엣지 방식 렌더링에 필요한 항목 데이터
 */
export function getEdgeArchitectureItem(t: TFunction): ArchitectureItem {
  return {
    title: t('엣지 분석서버 방식'),
    description: t(
      '효과적인 로컬 데이터 처리로 네트워크 대역폭을 절약하고 지연시간을 줄여 보다 수준 높은 사용자 경험을 제공합니다.',
    ),
    image: {
      mobile: {
        src: {
          ko: '/images/apps/technology/sec_04_img_01_m.svg',
          en: '/images/apps/technology/sec_04_img_01_en_m.svg',
        },
        width: 335,
        height: 594,
        enWidth: 335,
        enHeight: 630,
      },
      desktop: {
        src: {
          ko: '/images/apps/technology/sec_04_img_01.svg',
          en: '/images/apps/technology/sec_04_img_01_en.svg',
        },
        width: 1104,
        height: 265,
        enWidth: 1104,
        enHeight: 283,
      },
    },
    infoItems: [
      {
        title: t('제품 형태'),
        description: t('비명, 고성, 울음, 충돌, 유리 깨짐 등'),
      },
      {
        title: t('제품 구성'),
        description: t(
          '마이크, 엣지 분석서버, 중앙 관리서버, 모니터링 시스템(CMS)',
        ),
      },
      {
        title: t('모니터링 SW'),
        description: t('Listen AI 모니터링 시스템 제공'),
      },
      {
        title: t('요구사항'),
        description: t(
          '별도의 개발팀이나 개발 구현/연동 작업 없이 바로 사용 가능',
        ),
      },
    ],
    feature: {
      title: t('특징 및 장점'),
      items: [
        {
          title: t('엣지 장비 내부에서 AI 연산 처리'),
          description: t(
            '사운드 AI 분석 기술을 엣지 기기에 통합해 장비 내부에서 모든 사운드 AI 연산을 처리해 효과적인 로컬 데이터 처리가 가능합니다. 엣지기기의 연산 성능에 맞는 부하가 적은 AI 분석 기능에 추천합니다.',
          ),
        },
        {
          title: t('기존 방식 대비 대역폭 70% 절약'),
          description: t(
            '각 엣지 기기에서 서버로 오디오 클립을 보내는 데 약 280 kbps 정도의 대역폭이 소비됩니다. 장치 수가 증가하면 대역폭 요구도 증가하지만, 엣지 디바이스 시스템은 대역폭 요구사항을 약 1 kpbs 수준으로 대폭 줄였습니다.',
          ),
        },
        {
          title: t('개인정보보호'),
          description: t(
            '중앙 관리 서버로 소리를 전송하지 않고 로컬 엣지 분석서버의 분석 결과만 전송하기 때문에 개인정보보호 측면에서도 가장 안전한 방식입니다.',
          ),
        },
      ],
    },
  };
}

/**
 * ### getOnPremArchitectureItem
 *
 * Industrial 페이지에 이식하는 온프렘 방식 데이터.
 * 기존 `/technology` HowItWorksSection 의 두 번째 항목을 그대로 옮겼다.
 * 키는 `common` 네임스페이스의 기존 번역 키를 재사용하므로 로케일 추가가 없다.
 *
 * @param t `common` 네임스페이스 번역 함수
 * @returns 온프렘 방식 렌더링에 필요한 항목 데이터
 */
export function getOnPremArchitectureItem(t: TFunction): ArchitectureItem {
  return {
    title: t('온프렘 방식'),
    description: t(
      '중앙 분석 서버에서 AI 분석을 처리해 외부에 데이터가 노출되지 않고 적은 네트워크 비용과 지연시간이 장점입니다.',
    ),
    image: {
      mobile: {
        src: {
          ko: '/images/apps/technology/onprem-architecture-ko-m.svg',
          en: '/images/apps/technology/onprem-architecture-en-m.svg',
        },
        width: 262,
        height: 557,
      },
      desktop: {
        src: {
          ko: '/images/apps/technology/onprem-architecture-ko.svg',
          en: '/images/apps/technology/onprem-architecture-en.svg',
        },
        width: 1104,
        height: 265,
      },
    },
    infoItems: [
      {
        title: t('제품 형태'),
        description: t('마찰음, 엔진소리, 비정상 소음 등'),
      },
      {
        title: t('제품 구성'),
        description: t('마이크, 중앙 분석 서버, Deeply 모니터링 시스템'),
      },
      {
        title: t('모니터링 SW'),
        description: t('Listen AI 모니터링 시스템 제공'),
      },
      {
        title: t('요구사항'),
        description: t('현장 PLC/MES 및 외부 공장 시스템 연계 범위 사전 협의'),
      },
    ],
    feature: {
      title: t('특징 및 장점'),
      items: [
        {
          title: t('강력한 AI 분석 능력'),
          description: t(
            '엣지 분석서버 대비 훨씬 강력한 연산 능력으로 큰 규모의 AI 모델을 활용해 더욱 다양하고 정확한 AI 분석 기능을 적용할 수 있습니다. 특히 대규모 시스템을 구축할 때 온프렘 방식이 가장 적합합니다.',
          ),
        },
        {
          title: t('데이터 외부 유출 방지'),
          description: t(
            '온프렘 방식은 다른 방식과 다르게 소리 데이터를 인터넷으로 전송하지 않고 내부망 내에 있는 온프렘 서버로 전송합니다. 기본적으로 마이크를 통해 수집된 데이터는 내부망으로만 전송하기 때문에 외부로 데이터가 노출되지 않습니다.',
          ),
        },
        {
          title: t('경제적인 네트워크 운영'),
          description: t(
            '상대적으로 짧은 네트워크 지연시간으로 효율적인 데이터 전송이 가능하며, 인터넷으로 소리 데이터를 전송할 때에도 네트워크 비용이 발생하지 않아 네트워크를 효율적이고 경제적으로 운용할 수 있습니다. ',
          ),
        },
      ],
    },
  };
}
